import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizationContext } from './orgContext';
import config from './config';

type TutorCapabilitySeed = {
  slug: string;
  placeholderTitle: string;
  placeholderCaption: string;
};

type TutorCapabilityContent = {
  id: number;
  slug: string;
  title: string;
  summary?: string | null;
  htmlContent: string;
  mainImageUrl?: string | null;
  mainImageAlt?: string | null;
};

const ORG_ID = 1;

const tutorCapabilitySeeds: TutorCapabilitySeed[] = [
  {
    slug: 'how-it-works-courses-categories',
    placeholderTitle: 'Course and Category Admin',
    placeholderCaption: 'Replace with your course/category management screen.',
  },
  {
    slug: 'how-it-works-question-bank',
    placeholderTitle: 'Question Bank Workspace',
    placeholderCaption: 'Replace with your question list or question editor view.',
  },
  {
    slug: 'how-it-works-ai-authoring',
    placeholderTitle: 'AI Authoring Flow',
    placeholderCaption: 'Replace with AI-assisted creation screen and review panel.',
  },
  {
    slug: 'how-it-works-assignments',
    placeholderTitle: 'Assignment Distribution',
    placeholderCaption: 'Replace with assignment creation and class assignment screen.',
  },
  {
    slug: 'how-it-works-classes-students',
    placeholderTitle: 'Classes and Students',
    placeholderCaption: 'Replace with class list and student management view.',
  },
  {
    slug: 'how-it-works-site-content-analytics',
    placeholderTitle: 'Content and Analytics Dashboard',
    placeholderCaption: 'Replace with your site content admin and analytics screen.',
  },
];

const normalizeSlug = (value: string): string => value.trim().toLowerCase();

type BasicStepsView = 'tutor' | 'student';

type BasicStep = {
  number: string;
  title: string;
  description: string;
};

const basicSteps: Record<BasicStepsView, BasicStep[]> = {
  tutor: [
    {
      number: '1',
      title: 'Create courses, categories, and classes',
      description: 'Start by setting up the learning structure: create your courses, organize topics with categories, and group students into classes or batches.',
    },
    {
      number: '2',
      title: 'Add questions and learning content',
      description: 'Build your question bank, add explanations and diagrams, and use AI assistance to speed up category and question creation.',
    },
    {
      number: '3',
      title: 'Assign work and track student progress',
      description: 'Publish homework or tests to classes, monitor completion, and review analytics to identify weak topics and improve outcomes.',
    },
  ],
  student: [
    {
      number: '1',
      title: 'Choose your course',
      description: 'Pick the exam track you are preparing for and enter the right learning path.',
    },
    {
      number: '2',
      title: 'Practice topic by topic',
      description: 'Work through targeted questions with clear feedback and explanations.',
    },
    {
      number: '3',
      title: 'Take timed tests',
      description: 'Build speed and confidence with full-length mock exams and review your progress.',
    },
  ],
};

const ScreenshotPlaceholder: React.FC<{ title: string; caption: string }> = ({ title, caption }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-100 p-3 mt-4">
    <div className="h-[220px] md:h-[260px] w-full rounded-md border border-slate-300 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Screenshot Placeholder</p>
        <p className="mt-1 text-sm font-semibold text-slate-700">{title}</p>
      </div>
    </div>
    <p className="mt-2 text-xs text-slate-600">{caption}</p>
  </div>
);

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();
  const organizationContext = getOrganizationContext();
  const orgName = organizationContext?.organizationName || organizationContext?.organizationSlug || 'Exam Masters';
  const [basicStepsView, setBasicStepsView] = useState<BasicStepsView>('tutor');
  const [tutorCapabilityItems, setTutorCapabilityItems] = useState<TutorCapabilityContent[]>([]);
  const [loadingTutorCapabilities, setLoadingTutorCapabilities] = useState(true);
  const [tutorCapabilitiesError, setTutorCapabilitiesError] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadTutorCapabilities = async () => {
      try {
        setLoadingTutorCapabilities(true);
        setTutorCapabilitiesError(null);

        const response = await fetch(`${config.apiBaseUrl}/api/sitecontent?contentType=howitworks`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': String(ORG_ID),
          },
          mode: 'cors',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Tutor capabilities API responded with ${response.status}`);
        }

        const raw: unknown = await response.json();
        const allItems = Array.isArray(raw) ? (raw as TutorCapabilityContent[]) : [];
        const seedBySlug = new Map(tutorCapabilitySeeds.map((seed, index) => [seed.slug, index]));

        const filtered = allItems
          .filter((item) => seedBySlug.has(normalizeSlug(item.slug)))
          .sort((left, right) => {
            const leftRank = seedBySlug.get(normalizeSlug(left.slug)) ?? 999;
            const rightRank = seedBySlug.get(normalizeSlug(right.slug)) ?? 999;
            return leftRank - rightRank;
          });

        setTutorCapabilityItems(filtered);
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }

        setTutorCapabilitiesError(error instanceof Error ? error.message : 'Failed to load tutor capabilities');
        setTutorCapabilityItems([]);
      } finally {
        setLoadingTutorCapabilities(false);
      }
    };

    void loadTutorCapabilities();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!zoomedImage) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setZoomedImage(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [zoomedImage]);

  const seedMetaBySlug = useMemo(
    () => new Map(tutorCapabilitySeeds.map((seed) => [seed.slug, seed])),
    []
  );

  return (
    <div className="bg-gradient-to-br from-app-page-from to-app-page-to min-h-screen">
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">How It Works</span>
        </div>
      </div>

      <div className="px-3 py-3 md:px-4 lg:px-6">
        <div className="mx-auto max-w-6xl rounded-xl border border-white/40 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 md:px-6 md:py-4">
            <p className="text-xs font-semibold tracking-wider uppercase text-white/85">How It Works</p>
            <h1 className="mt-1 text-lg md:text-xl font-bold leading-tight">Student Learning + Tutor Operations</h1>
            <p className="mt-1 text-xs md:text-sm text-white/90 max-w-3xl">
              {orgName} supports exam prep for students and daily workflow management for tutors in one platform.
            </p>
          </div>

          <div className="p-4 md:p-6 space-y-6">
            <section className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50 text-slate-900 p-5 md:p-7 shadow-[0_14px_34px_rgba(79,70,229,0.10)]">
              <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.9fr] gap-5 items-start">
                <div className="text-left">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Platform Overview</p>
                  <h2 className="mt-2 text-2xl md:text-3xl font-bold leading-tight">Run tutoring operations and student practice from one place</h2>
                  <p className="mt-3 text-sm md:text-base text-slate-700 max-w-2xl leading-relaxed">
                    Set up courses and classes, build question banks, assign homework and tests, and track progress with clear analytics. Students follow a structured path from practice to timed exams.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-full border border-indigo-200 bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">Tutor-first workflow</span>
                    <span className="rounded-full border border-indigo-200 bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">Question bank + AI authoring</span>
                    <span className="rounded-full border border-indigo-200 bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">Assignments + analytics</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="rounded-xl border border-indigo-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">For Tutors</p>
                    <p className="mt-2 text-sm text-slate-700">Create structure, publish content, assign work, and monitor outcomes across classes.</p>
                  </div>
                  <div className="rounded-xl border border-indigo-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">For Students</p>
                    <p className="mt-2 text-sm text-slate-700">Practice by topic, sit timed tests, and build confidence with measurable progress.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 text-left">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Basic Steps</p>
              <h2 className="mt-1 text-xl md:text-2xl font-bold text-slate-900">How the system works at a glance</h2>
              <div className="mt-4 inline-flex rounded-lg border border-indigo-200 bg-indigo-50 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setBasicStepsView('tutor')}
                  className={`px-3 py-2 text-xs md:text-sm font-semibold rounded-md transition-colors ${
                    basicStepsView === 'tutor' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-100'
                  }`}
                >
                  For Tutors
                </button>
                <button
                  type="button"
                  onClick={() => setBasicStepsView('student')}
                  className={`px-3 py-2 text-xs md:text-sm font-semibold rounded-md transition-colors ${
                    basicStepsView === 'student' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-100'
                  }`}
                >
                  For Students
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {basicSteps[basicStepsView].map((step) => (
                  <div
                    key={step.number}
                    className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 md:p-6 shadow-[0_16px_40px_rgba(15,23,42,0.08)]"
                  >
                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white text-sm font-bold shadow-sm">
                      {step.number}
                    </div>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Step {step.number}</p>
                    <h3 className="mt-2 text-lg md:text-xl font-bold text-slate-900 leading-snug">{step.title}</h3>
                    <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">{step.description}</p>
                    <div className="mt-4 h-1.5 w-16 rounded-full bg-indigo-100">
                      <div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${Number(step.number) * 33}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed max-w-4xl">
                {basicStepsView === 'tutor'
                  ? 'This gives tutors one clear workflow: structure the curriculum, prepare content, assign it to students, and use progress data to guide the next lesson.'
                  : 'Practice topic by topic, take tests, and track progress as you prepare.'}
              </p>
            </section>

            <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:p-5">
              <div className="text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tutor Capabilities</p>
                <h2 className="mt-1 text-xl md:text-2xl font-bold text-slate-900">Everything tutors can manage before students even log in</h2>
                <p className="mt-2 text-sm text-slate-600 max-w-3xl">
                  This public preview helps parents, institutes, and tutors understand the full platform workflow before registration.
                </p>
              </div>

              {loadingTutorCapabilities && (
                <div className="mt-4 text-sm text-slate-600 text-left">Loading tutor capabilities...</div>
              )}

              {tutorCapabilitiesError && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 text-left">
                  {tutorCapabilitiesError}
                </div>
              )}

              <div className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
                {tutorCapabilityItems.map((item) => {
                  const seedMeta = seedMetaBySlug.get(normalizeSlug(item.slug));

                  return (
                  <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-4 text-left">
                    <h3 className="text-base md:text-lg font-bold text-slate-900">{item.title}</h3>
                    {item.summary && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.summary}</p>}
                    <div
                      className="mt-3 text-sm text-slate-700 leading-relaxed max-w-none [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: item.htmlContent }}
                    />
                    {item.mainImageUrl ? (
                      <div className="mt-4 relative group overflow-hidden rounded-lg border border-slate-200">
                        <img
                          src={item.mainImageUrl}
                          alt={item.mainImageAlt || item.title}
                          className="w-full h-[200px] sm:h-[220px] md:h-[260px] lg:h-[280px] object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04] cursor-zoom-in"
                          onClick={() => setZoomedImage({ src: item.mainImageUrl as string, alt: item.mainImageAlt || item.title })}
                        />
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setZoomedImage({ src: item.mainImageUrl as string, alt: item.mainImageAlt || item.title });
                          }}
                          className="absolute top-2 right-2 rounded-md bg-slate-900/75 px-2 py-1 text-xs font-semibold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus:opacity-100"
                          aria-label="Open larger image"
                        >
                          Zoom
                        </button>
                      </div>
                    ) : (
                      <ScreenshotPlaceholder
                        title={seedMeta?.placeholderTitle || item.title}
                        caption={seedMeta?.placeholderCaption || 'Add an image from Manage Site Content.'}
                      />
                    )}
                  </article>
                  );
                })}
              </div>

              {!loadingTutorCapabilities && tutorCapabilityItems.length === 0 && !tutorCapabilitiesError && (
                <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 text-left">
                  No tutor capability rows found for org {ORG_ID} with content type HowItWorks.
                </div>
              )}
            </section>

            {zoomedImage && (
              <div
                className="fixed inset-0 z-[1200] bg-slate-950/75 p-4 md:p-8 flex items-center justify-center"
                onClick={() => setZoomedImage(null)}
                role="dialog"
                aria-modal="true"
                aria-label="Image preview"
              >
                <div
                  className="relative max-w-6xl w-full bg-white rounded-xl shadow-2xl overflow-hidden"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setZoomedImage(null)}
                    className="absolute top-3 right-3 z-10 rounded-md bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-white"
                  >
                    Close
                  </button>
                  <div className="bg-slate-100 p-2 md:p-3">
                    <img
                      src={zoomedImage.src}
                      alt={zoomedImage.alt}
                      className="w-full max-h-[84vh] object-contain rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            <section className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 md:p-5 text-left">
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Call To Action</p>
              <h2 className="mt-1 text-xl md:text-2xl font-bold text-indigo-900">Ready to explore more?</h2>
              <p className="mt-2 text-sm text-indigo-800">
                Register to start using the full tutor and student platform.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  Register
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
                >
                  Contact Us
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
